import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { confirmationRedirect, rememberPendingEmail } from '../../features/auth/pending-email';
import { fetchGeoCountry, SignUpForm, toSignUpRequest } from '../../features/auth/sign-up-form';
import { supabase } from '../../shared/api/supabase';
import { AuthLayout } from '../../shared/ui/auth-layout';

export function SignUpPage() {
  const { t } = useTranslation('auth');
  useDocumentTitle(t('signUp.title'));
  const navigate = useNavigate();
  // Prefills the country; the form works without it and never lets it override a choice.
  const geo = useQuery({
    queryKey: ['geo'],
    queryFn: fetchGeoCountry,
    staleTime: Infinity,
    retry: false,
  });
  return (
    <AuthLayout
      title={t('signUp.title')}
      footer={
        <p>
          {t('signUp.haveAccount')} <Link to="/sign-in">{t('signUp.signIn')}</Link>
        </p>
      }
    >
      <SignUpForm
        {...(geo.data ? { defaultCountry: geo.data } : {})}
        onSubmit={async (values) => {
          const request = toSignUpRequest(values);
          const { data, error } = await supabase.auth.signUp({
            ...request,
            options: { ...request.options, emailRedirectTo: confirmationRedirect() },
          });
          // An address that is already registered must look exactly like a new one. Hosted Supabase returns an
          // obfuscated user for it; the local stack raises `user_already_exists` instead, so that is folded in.
          if (error && error.code !== 'user_already_exists') return { error: error.message };
          // Email confirmation is on: no session until the link is followed.
          if (data.session) navigate('/');
          else {
            rememberPendingEmail(values.email);
            navigate('/check-email');
          }
          return { error: null };
        }}
      />
    </AuthLayout>
  );
}
